import {
  RichUtils,
  EditorState,
  ContentState,
  Modifier,
  BlockMapBuilder,
  CharacterMetadata,
  convertFromHTML,
} from "draft-js";

import { List, Repeat } from "immutable";

import isActiveEntity from "editor/utils/isActiveEntity";
import getActiveEntityRange from "editor/utils/getActiveEntityRange";
import getActiveEditorState from "editor/utils/getActiveEditorState";
import getActiveBlockType from "editor/utils/getActiveBlockType";
import getActiveBlockKey from "editor/utils/getActiveBlockKey";
import forceEditorSelection from "editor/utils/forceEditorSelection";
import {
  ENTITY_TYPE,
  BLOCK_TYPE,
  ON_RETURN,
  SELECTABLE_BLOCKS,
  CAPTION_BLOCKS,
  BLOCKS_WITH_DATA,
  KEY_COMMANDS,
  EDITOR_MODALS,
} from "config/constants/editor";
import { updateActiveEditor } from "editor/actions/form";
import { toggleModal } from "editor/actions/ui";
import { insertCustomBlocks, removeQuote } from "editor/actions/blocks";

export const HANDLED = "handled";
export const NOT_HANDLED = "not-handled";

export const handleKeyCommand = command => {
  return (dispatch, getState) => {
    let editorState = getActiveEditorState(getState());
    const selectionState = editorState.getSelection();

    //override default backspace behavior for quote/attribution block types
    //but only if cursor is at offset 0 and collapsed
    if (
      command === "backspace" &&
      !selectionState.getAnchorOffset() &&
      selectionState.isCollapsed()
    ) {
      const activeBlockType = getActiveBlockType(editorState);
      const activeBlockKey = getActiveBlockKey(editorState);
      const contentState = editorState.getCurrentContent();
      const prevBlock = contentState.getBlockBefore(activeBlockKey);

      if (activeBlockType === BLOCK_TYPE.QUOTE_ATTRIBUTION) {
        //move cursor into the preceding quote block
        const blockBefore = contentState.getBlockBefore(
          getActiveBlockKey(editorState)
        );
        const selectionOptions = {
          anchorKey: blockBefore.getKey(),
          anchorOffset: blockBefore.getText().length,
        };
        dispatch(setEditorSelection(selectionOptions));
        return HANDLED;
      } else if (ON_RETURN.HANDLE_QUOTE.includes(activeBlockType)) {
        const activeBlockKey = getActiveBlockKey(editorState);
        dispatch(removeQuote(activeBlockKey));
        return HANDLED;
      } else if (prevBlock && SELECTABLE_BLOCKS.includes(prevBlock.getType())) {
        return HANDLED;
      }
    }

    if (command === KEY_COMMANDS.LINK) {
      dispatch(toggleModal(EDITOR_MODALS.LINK_EDITOR));
      return HANDLED;
    }

    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      dispatch(updateActiveEditor(newState));
      return HANDLED;
    }
    return NOT_HANDLED;
  };
};

export const toggleInlineStyle = style => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    dispatch(
      updateActiveEditor(RichUtils.toggleInlineStyle(editorState, style))
    );
  };
};

export const toggleBlockType = style => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const activeBlockType = getActiveBlockType(editorState);
    if (
      style !== BLOCK_TYPE.PLAIN_QUOTE &&
      (activeBlockType === BLOCK_TYPE.PLAIN_QUOTE ||
        activeBlockType === BLOCK_TYPE.QUOTE_ATTRIBUTION)
    ) {
      const contentState = editorState.getCurrentContent();
      const activeBlockKey = getActiveBlockKey(editorState);
      const quoteBlockKey =
        activeBlockType === BLOCK_TYPE.QUOTE_ATTRIBUTION
          ? contentState.getKeyBefore(activeBlockKey)
          : activeBlockKey;
      dispatch(removeQuote(quoteBlockKey, style));
    } else {
      dispatch(
        updateActiveEditor(RichUtils.toggleBlockType(editorState, style))
      );
    }
  };
};

export const toggleLink = (url, text) => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const contentStateWithEntity = contentState.createEntity(
      ENTITY_TYPE.LINK,
      "MUTABLE",
      { url }
    );
    const currentInlineStyle = editorState.getCurrentInlineStyle();
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const isCollapsed = selection.isCollapsed(); /* false on new links */

    if (
      isActiveEntity(editorState, ENTITY_TYPE.LINK) ||
      isActiveEntity(editorState, ENTITY_TYPE.OLD_LINK)
    ) {
      const entityRange = getActiveEntityRange(editorState);
      const contentStateWithEntity = Modifier.replaceText(
        contentState,
        entityRange,
        text,
        currentInlineStyle,
        entityKey
      );
      dispatch(
        updateActiveEditor(
          EditorState.push(
            editorState,
            contentStateWithEntity,
            "insert-characters"
          )
        )
      );
    } else if (isCollapsed && text) {
      const contentStateWithEntity = Modifier.insertText(
        contentState,
        selection,
        text,
        currentInlineStyle,
        entityKey
      );
      dispatch(
        updateActiveEditor(
          EditorState.push(
            editorState,
            contentStateWithEntity,
            "insert-characters"
          )
        )
      );
    } else if (!isCollapsed && text) {
      const contentStateWithEntity = Modifier.replaceText(
        contentState,
        selection,
        text,
        currentInlineStyle,
        entityKey
      );
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      dispatch(
        updateActiveEditor(
          RichUtils.toggleLink(
            newEditorState,
            newEditorState.getSelection(),
            entityKey
          )
        )
      );
    } else if (!isCollapsed) {
      const newEditorState = EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      });
      dispatch(
        updateActiveEditor(
          RichUtils.toggleLink(
            newEditorState,
            newEditorState.getSelection(),
            entityKey
          )
        )
      );
    }
  };
};

export const removeLink = () => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const entityRange = getActiveEntityRange(editorState);
    dispatch(
      updateActiveEditor(RichUtils.toggleLink(editorState, entityRange, null))
    );
  };
};

export const onTab = e => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const newState = RichUtils.onTab(e, editorState, 1);
    return dispatch(updateActiveEditor(newState));
  };
};

export const setEditorSelection = selectionOptions => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const newEditorState = forceEditorSelection(editorState, selectionOptions);
    return dispatch(updateActiveEditor(newEditorState));
  };
};

export const handleReturn = () => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const activeBlockType = getActiveBlockType(editorState);
    const activeBlockKey = getActiveBlockKey(editorState);
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const activeBlock = contentState.getBlockForKey(activeBlockKey);
    const blockText = activeBlock.getText();

    if (
      CAPTION_BLOCKS.includes(activeBlockType) &&
      selectionState.getEndOffset() !== blockText.length
    ) {
      return HANDLED;
    }
    if (ON_RETURN.NEXT_BLOCK_UNSTYLED.includes(activeBlockType)) {
      const startOffset = selectionState.getStartOffset();
      const endOffset = selectionState.getEndOffset();
      const blockData = activeBlock.getData();
      const characterList = activeBlock.getCharacterList();

      const selectionOptions = {
        safetyBlock: false,
        forceNoSplit: true,
        selectionOffset: 0,
        removeFullBlocks: {
          startKey: activeBlockKey,
          endKey: activeBlockKey,
        },
      };

      if (endOffset === blockText.length) {
        const newText = blockText.slice(0, startOffset);
        dispatch(
          insertCustomBlocks(
            [
              {
                type: activeBlockType,
                text: newText,
                data: blockData,
                characterList,
              },
              {
                type: BLOCK_TYPE.UNSTYLED,
              },
            ],
            selectionOptions
          )
        );
      } else if (startOffset === 0) {
        const newText = blockText.slice(endOffset);
        dispatch(
          insertCustomBlocks(
            [
              {
                type: BLOCK_TYPE.UNSTYLED,
              },
              {
                type: activeBlockType,
                text: newText,
                data: blockData,
                characterList,
              },
            ],
            selectionOptions
          )
        );
      } else {
        const firstBlockText = blockText.slice(0, startOffset);
        const secondBlockText = blockText.slice(endOffset);
        const firstBlockCharacterList = characterList.slice(0, startOffset);
        const secondBlockCharacterList = characterList.slice(endOffset);

        dispatch(
          insertCustomBlocks(
            [
              {
                type: activeBlockType,
                text: firstBlockText,
                data: blockData,
                characterList: firstBlockCharacterList,
              },
              {
                type: BLOCK_TYPE.UNSTYLED,
                text: secondBlockText,
                characterList: secondBlockCharacterList,
              },
            ],
            selectionOptions
          )
        );
      }
      return HANDLED;
    } else if (ON_RETURN.HANDLE_QUOTE.includes(activeBlockType)) {
      if (!blockText) {
        dispatch(removeQuote(activeBlockKey));
      } else {
        const anchorOffset = editorState.getSelection().getAnchorOffset();
        //if empty block or cursor at end of text, move to attribution block
        if (!blockText || anchorOffset === blockText.length) {
          const blockAfter = contentState.getBlockAfter(activeBlockKey);
          if (
            blockAfter &&
            blockAfter.getType() === BLOCK_TYPE.QUOTE_ATTRIBUTION
          ) {
            const selectionOptions = {
              anchorKey: blockAfter.getKey(),
              anchorOffset: blockAfter.getText().length,
            };
            dispatch(setEditorSelection(selectionOptions));
          } else {
            //if there was no attribution, insert one
            dispatch(
              insertCustomBlocks(
                [
                  {
                    type: BLOCK_TYPE.QUOTE_ATTRIBUTION,
                  },
                ],
                {
                  selectionIndex: 0,
                  selectionOffset: 0,
                }
              )
            );
          }
        } else {
          dispatch(
            insertCustomBlocks(
              [
                {
                  type: BLOCK_TYPE.QUOTE_ATTRIBUTION,
                },
                {
                  type: activeBlockType,
                },
              ],
              {
                selectionIndex: 1,
                selectionOffset: 0,
                safetyBlock: false,
              }
            )
          );
        }
      }
      return HANDLED;
    }

    return NOT_HANDLED;
  };
};

const pasteFirstTextBlock = (text, html) => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const contentState = editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(
      getActiveBlockKey(editorState)
    );

    let firstBlock;
    if (html) {
      const dataFromHTML = convertFromHTML(html);
      //get the first block that has text
      //copying a full line from MS Word auto inserts a newline
      firstBlock =
        dataFromHTML.contentBlocks.find(block => {
          return !!block.getText().trim().length;
        }) || dataFromHTML.contentBlocks[0];
    } else {
      const newContentState = ContentState.createFromText(text);
      firstBlock = newContentState.getFirstBlock();
    }

    //because these are caption blocks, we should only insert the first block and merge the data
    const firstBlockWithData = firstBlock.merge({
      data: currentBlock.getData(),
    });
    const fragment = BlockMapBuilder.createFromArray([firstBlockWithData]);
    const pastedState = Modifier.replaceWithFragment(
      contentState,
      editorState.getSelection(),
      fragment
    );
    dispatch(
      updateActiveEditor(
        EditorState.push(editorState, pastedState, "insert-fragment")
      )
    );
  };
};

export const handlePastedText = (text, html) => {
  return (dispatch, getState) => {
    const editorState = getActiveEditorState(getState());
    const contentState = editorState.getCurrentContent();
    const currentBlock = contentState.getBlockForKey(
      getActiveBlockKey(editorState)
    );

    //data is deleted unless we override default pasting
    if (BLOCKS_WITH_DATA.includes(currentBlock.getType())) {
      dispatch(pasteFirstTextBlock(text, html));
      return HANDLED;
    }

    //subheaders should not be bold when pasted between draft instances
    if (html) {
      const dataFromHTML = convertFromHTML(html);
      const containsHeader = dataFromHTML.contentBlocks.find(block => {
        return block.getType() === BLOCK_TYPE.SUBHEAD;
      });
      if (containsHeader) {
        const noStyleSubheaders = dataFromHTML.contentBlocks.map(block => {
          if (block.getType() === BLOCK_TYPE.SUBHEAD) {
            const charData = CharacterMetadata.create();
            return block.merge({
              characterList: List(Repeat(charData, block.text.length)),
            });
          }
          return block;
        });

        const fragment = BlockMapBuilder.createFromArray(noStyleSubheaders);
        const pastedState = Modifier.replaceWithFragment(
          contentState,
          editorState.getSelection(),
          fragment
        );
        dispatch(
          updateActiveEditor(
            EditorState.push(editorState, pastedState, "insert-fragment")
          )
        );
        return HANDLED;
      }
    }
    return NOT_HANDLED;
  };
};

export const inlineHandlePastedText = (text, html) => {
  return dispatch => {
    dispatch(pasteFirstTextBlock(text, html));
    return HANDLED;
  };
};
